import { HotkeyError, isReservedHotkey, normalizeHotkey } from "@goalhorns/bundle";
import { useEffect, useRef, useState } from "react";
import type { MessageKey } from "../../i18n/en";
import { useI18n } from "../../i18n/I18nProvider";
import { isMacPlatform } from "../../lib/platform";
import { X } from "lucide-react";
import { IconButton } from "../../components/ui/IconButton";
import { displayHotkey } from "./hotkey-display";
import { useHotkeyRecorder } from "./useHotkeyRecorder";

export interface HotkeyRecorderProps {
  /** The player's name, used in the clear button's accessible name. */
  name: string;
  value: string | null;
  disabled: boolean;
  onChange: (canonical: string | null) => void;
}

/**
 * One chip: the chip is the button. Empty it reads "Set hotkey"; a click starts recording.
 * Set, it shows the combo and a small × inside its right edge clears it.
 * Recording: press the keys, release them all. The peak combo is saved on release.
 * While recording, the held keys show live in a focused capture field; Escape cancels.
 * The canonical form comes from @goalhorns/bundle, so the UI and the export agree.
 */
export function HotkeyRecorder({ name, value, disabled, onChange }: HotkeyRecorderProps) {
  const { t } = useI18n();
  const [recording, setRecording] = useState(false);
  const [live, setLive] = useState<readonly string[]>([]);
  const [message, setMessage] = useState<MessageKey | null>(null);
  const captureRef = useRef<HTMLSpanElement>(null);
  const isMac = isMacPlatform();

  useEffect(() => {
    if (recording) captureRef.current?.focus();
  }, [recording]);

  useHotkeyRecorder(recording, {
    onPending: setLive,
    onCommit: (tokens) => {
      setRecording(false);
      setLive([]);
      const parsed = parseCombo(tokens);
      if (parsed.kind === "invalid") {
        setMessage("error.hotkey_invalid");
      } else if (parsed.kind === "reserved") {
        setMessage("error.hotkey_reserved");
      } else {
        setMessage(null);
        onChange(parsed.canonical);
      }
    },
    onCancel: () => {
      setRecording(false);
      setLive([]);
    },
    onIgnore: () => {
      setLive([]);
      setMessage("editor.hotkeyIgnored");
    },
  });

  return (
    <div role="group" aria-label={t("editor.hotkey")} className="flex min-w-0 flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        {recording ? (
          <span
            ref={captureRef}
            tabIndex={-1}
            aria-live="polite"
            className="inline-flex min-h-9 min-w-44 items-center rounded-control border-2 border-blue bg-board px-3 font-display text-note font-bold tracking-[0.04em] text-ink uppercase outline-none"
          >
            {live.length > 0 ? displayHotkey(live.join("+"), isMac) : t("editor.hotkeyPress")}
          </span>
        ) : (
          <span className="inline-flex min-h-11 items-center rounded-control border border-edge bg-board pr-1">
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                setMessage(null);
                setRecording(true);
              }}
              className="inline-flex min-h-11 items-center rounded-control px-3 font-display text-note font-bold tracking-[0.04em] uppercase hover:bg-blue-wash disabled:cursor-not-allowed"
            >
              {value ? (
                <kbd className="font-bold text-ink not-italic">{displayHotkey(value, isMac)}</kbd>
              ) : (
                <span className="text-ink-3">{t("editor.setHotkey")}</span>
              )}
            </button>
            {value ? (
              <IconButton
                label={t("editor.clearHotkey", { name })}
                icon={<X aria-hidden="true" className="size-4" strokeWidth={1.75} />}
                disabled={disabled}
                className="size-9!"
                onClick={() => {
                  setMessage(null);
                  onChange(null);
                }}
              />
            ) : null}
          </span>
        )}
      </div>
      {recording ? <p className="text-note text-ink-2">{t("editor.hotkeyHint")}</p> : null}
      {message ? (
        <p role="alert" className="text-note font-medium text-red">
          {t(message)}
        </p>
      ) : null}
    </div>
  );
}

type ParsedCombo = { kind: "ok"; canonical: string } | { kind: "invalid" } | { kind: "reserved" };

/** Canonicalises the pressed keys with the bundle rules and refuses reserved combos. */
function parseCombo(tokens: readonly string[]): ParsedCombo {
  try {
    const canonical = normalizeHotkey(tokens.join("+"));
    if (canonical === null) return { kind: "invalid" };
    return isReservedHotkey(canonical) ? { kind: "reserved" } : { kind: "ok", canonical };
  } catch (error: unknown) {
    if (error instanceof HotkeyError) return { kind: "invalid" };
    throw error;
  }
}
